// RunnerFailure — how a runner process ended when it did not end well
// (docs/plans/2026-10-02-checkpoints-en-overdracht.md §3.1, contract
// docs/plans/2026-10-02-checkpoints-contract.md §3.1).
//
// Every executor parser (worker, work package 1) turns a failed runner exit
// into one of these; the worker core (package 2) acts on `kind`, the API
// (package 3) on quota/auth reports. Browser-safe: plain zod + pure helpers.

import { z } from 'zod'

/**
 * - `transient`: 529 overloaded, 5xx, connection reset, aborted stream — same runner, same container, retry with backoff.
 * - `rate_limit`: 429 per minute/tokens — as `transient` with a longer backoff; after `maxTransientRetries` it is reported as `quota`.
 * - `quota`: a subscription usage limit / credits gone — checkpoint, mark the connection exhausted, wait or hand off.
 * - `auth`: token expired/revoked, login invalid — checkpoint, run `blocked`, connection needs a new login.
 * - `fatal`: everything else — `failed`, but with a checkpoint so "resume" can continue.
 */
export const RunnerFailureKindSchema = z.enum(['transient', 'rate_limit', 'quota', 'auth', 'fatal'])
export type RunnerFailureKind = z.infer<typeof RunnerFailureKindSchema>

export const RUNNER_FAILURE_KINDS: readonly RunnerFailureKind[] = RunnerFailureKindSchema.options

/** Max length of `RunnerFailure.message` / `.detail` (the parser truncates; never the full stream). */
export const RUNNER_FAILURE_MESSAGE_MAX = 2000

export const RunnerFailureSchema = z.object({
  kind: RunnerFailureKindSchema,
  /** One human-readable line (or a few), already redacted — shown on the timeline. */
  message: z.string().min(1).max(RUNNER_FAILURE_MESSAGE_MAX),
  /** When the limit resets, if the runner said so (ISO 8601). Absent = unknown; see `quotaWaitMs`. */
  resetsAt: z.string().datetime({ offset: true }).optional(),
  /** A `Retry-After`-style hint for `rate_limit`/`transient`, in ms, if the runner gave one. */
  retryAfterMs: z.number().int().nonnegative().optional(),
  /** The API/subscription behind the runner: 'anthropic', 'openai', 'openai-codex', 'openai-compatible', ... */
  provider: z.string().min(1).optional(),
  /** The executor connection (`credentials.id`, the payload's `executor.credentialProfileId`). Set by the worker, not the parser. */
  connectionId: z.string().uuid().optional(),
  /** Which runner produced it ('claude-cli' | 'codex-cli' | 'pi'). Set by the worker. */
  runner: z.string().min(1).optional(),
  /** Short raw excerpt (redacted) the classification was based on — for debugging the classifier, not for the UI. */
  detail: z.string().max(RUNNER_FAILURE_MESSAGE_MAX).optional(),
})
export type RunnerFailure = z.infer<typeof RunnerFailureSchema>

/** Same-container retry backoff for `transient` (design §3.3: 30 s, 2 min, 8 min). Index = attempt - 1. */
export const TRANSIENT_RETRY_DELAYS_MS: readonly number[] = [30_000, 120_000, 480_000]
/** `rate_limit` waits twice as long as `transient` (decision, contract §3.1). */
export const RATE_LIMIT_RETRY_DELAYS_MS: readonly number[] = [60_000, 240_000, 960_000]

/**
 * Backoff before same-container retry number `attempt` (1-based). A
 * `retryAfterMs` hint from the runner wins when it is longer. Attempts past
 * the table reuse its last entry (a role may allow up to 5 retries).
 */
export function runnerRetryDelayMs(kind: 'transient' | 'rate_limit', attempt: number, retryAfterMs?: number): number {
  const table = kind === 'rate_limit' ? RATE_LIMIT_RETRY_DELAYS_MS : TRANSIENT_RETRY_DELAYS_MS
  const index = Math.min(Math.max(attempt, 1), table.length) - 1
  const base = table[index] ?? table[table.length - 1] ?? 30_000
  return Math.max(base, retryAfterMs ?? 0)
}

/** Without a `resetsAt`: wait 15 min, doubling per consecutive quota wait of the same run, at most 4 h (design §3.1). */
export const QUOTA_WAIT_INITIAL_MS = 15 * 60_000
export const QUOTA_WAIT_MAX_MS = 4 * 60 * 60_000

/**
 * When a quota-paused run may resume: `resetsAt` if the runner gave one
 * (never in the past), else 15 min × 2^(waits already done), capped at 4 h.
 * `previousWaits` = how many times this run already waited on a quota.
 */
export function quotaResumeAt(failure: Pick<RunnerFailure, 'resetsAt'>, now: Date, previousWaits = 0): Date {
  if (failure.resetsAt) {
    const resets = new Date(failure.resetsAt)
    if (!Number.isNaN(resets.getTime()) && resets.getTime() > now.getTime()) return resets
  }
  const ms = Math.min(QUOTA_WAIT_INITIAL_MS * 2 ** Math.max(previousWaits, 0), QUOTA_WAIT_MAX_MS)
  return new Date(now.getTime() + ms)
}

/** Why a connection is temporarily unusable (`credentials.exhausted_reason`). */
export const ConnectionExhaustedReasonSchema = z.enum(['quota', 'rate_limit', 'auth'])
export type ConnectionExhaustedReason = z.infer<typeof ConnectionExhaustedReasonSchema>

/**
 * A connection's exhaustion as the API presents it (executor connections,
 * agents). Unusable while `exhaustedReason === 'auth'` (until a new login
 * clears it) or while `exhaustedUntil` is in the future.
 */
export interface ConnectionExhaustion {
  exhaustedUntil: string | null
  exhaustedReason: ConnectionExhaustedReason | null
}

/** Pure dispatch rule shared by API and web (contract §3.4). */
export function isConnectionExhausted(exhaustion: ConnectionExhaustion, now: Date): boolean {
  if (exhaustion.exhaustedReason === 'auth') return true
  if (!exhaustion.exhaustedUntil) return false
  return new Date(exhaustion.exhaustedUntil).getTime() > now.getTime()
}
